import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';

type ApplyState = 'idle' | 'pending' | 'analyzing' | 'validating' | 'deploying' | 'done';
type WindowState = 'default' | 'minimized' | 'expanded';

interface BoundaryText {
  analysis: string;
  attackChain: string;
  attackSteps: string[];
  impact: string;
  testing: string;
}

const BOUNDARY_TEXTS: Record<string, BoundaryText> = {
  'rsa-demo-issue-003': {
    analysis:
      "I've analyzed this attack chain and prepared a boundary to block it. Here's what I found: Your Web-to-Lead form is publicly accessible without authentication, but the Agentforce agent processing those submissions has full CRM read/write access plus outbound email capabilities. The attacker exploited this trust boundary by embedding adversarial instructions in the form's Description field. This untrusted public input directly influenced privileged agent execution, leading to CRM data exfiltration and corruption. The agent was manipulated to:",
    attackChain: 'untrusted input → sensitive data query → external transmission',
    attackSteps: [
      'Enumerate its tools and internal logic (reconnaissance)',
      'Query and exfiltrate 20 enterprise leads to an external email (data theft)',
      'Modify 312 lead records under the guise of compliance (data poisoning)',
    ],
    impact:
      'This boundary would have <strong>blocked this attack</strong> at step 3, before the agent queried bulk Lead data or sent outbound emails with CRM content. It would have also stopped the mass update operation at step 4. It would have <strong>prevented 6 similar externally-triggered exfiltration attempts</strong> detected in the last 30 days from public forms and unauthenticated sources.',
    testing:
      "I've tested it against <strong>3,800 normal Agentforce Lead processing sessions</strong>, including 920 where legitimate prospects submitted complex product inquiries or technical specifications that resembled injection patterns. Zero disruption to legitimate workflows — the agent can still process real leads, send confirmation emails to submitters, and update lead status normally.",
  },
  'rsa-demo-issue-001': {
    analysis:
      "I've analyzed this attack and prepared a boundary to prevent it from happening again. Here's what I found: The attacker embedded adversarial instructions in what looked like a normal conversation, manipulating the agent's reasoning to disclose internal system information. Your agent revealed its system prompt, available tools, pricing logic, and API endpoints because it couldn't distinguish between legitimate internal debugging requests and external reconnaissance probing.",
    attackChain: 'adversarial conversation → reasoning manipulation → information disclosure',
    attackSteps: [],
    impact:
      'This boundary would have <strong>blocked this attack</strong> at step 2, stopping the agent from including internal configuration details in its response before they were sent to the external user. It would have <strong>prevented 8 similar information disclosure attempts</strong> detected in the last 30 days, where attackers used social engineering techniques to extract system internals.',
    testing:
      "I've tested it against <strong>6,400 normal customer conversations</strong> — zero disruption to legitimate booking workflows, with under 50ms latency. Customers can still get flight information, pricing quotes, and booking assistance without any friction.",
  },
  'rsa-demo-issue-002': {
    analysis:
      "I've analyzed this attack and prepared a boundary to prevent it. Here's what I found: The developer cloned a repository containing a malicious README.md file with hidden instructions in an HTML comment. When Claude Code read the file, it parsed the payload and corrupted the AI's reasoning, leading to destructive MCP operations. The AI attempted to execute DROP TABLE commands against your production database, and the developer—experiencing approval fatigue—clicked through the MCP prompt without carefully reviewing the SQL.",
    attackChain: 'tainted repository → corrupted AI reasoning → destructive MCP operations',
    attackSteps: [],
    impact:
      'This boundary would have <strong>blocked this attack</strong> at step 2, before the destructive SQL was executed via MCP, even though the developer approved the action. It would have <strong>prevented 11 similar tainted-input-to-destructive-operation attempts</strong> detected in the last 30 days, including DROP commands, file deletions, and configuration changes triggered by external code.',
    testing:
      "I've tested it against <strong>4,200 normal Claude Code development sessions</strong> — zero disruption to legitimate database queries, schema migrations, or admin tasks. Developers can still use MCP tools for their intended purpose without interference.",
  },
  'rsa-demo-issue-006': {
    analysis:
      "I analyzed this attack chain and prepared a boundary to block it. Your Web-to-Lead form is publicly accessible, but the Agentforce agent processing submissions has full CRM read/write access and outbound email capabilities. An attacker embedded adversarial instructions in the form's Description field, causing the agent to:",
    attackChain: 'untrusted input → sensitive data query → external transmission',
    attackSteps: [
      'Enumerate its tools and internal logic (reconnaissance)',
      'Exfiltrate 20 enterprise leads to an external email (data theft)',
      'Modify 312 lead records under the guise of compliance (data poisoning)',
    ],
    impact:
      'This boundary would have <strong>blocked the attack</strong> at step 3, before bulk Lead data was queried or emailed. It would also have prevented the mass record update and <strong>6 similar exfiltration attempts</strong> detected in the last 30 days from public forms.',
    testing:
      'Tested against <strong>3,800 normal Lead processing sessions</strong>, including 920 with complex submissions resembling injection patterns. Zero disruption to legitimate workflows.',
  },
};

BOUNDARY_TEXTS['rsa-demo-issue-004'] = BOUNDARY_TEXTS['rsa-demo-issue-001'];
BOUNDARY_TEXTS['rsa-demo-issue-005'] = BOUNDARY_TEXTS['rsa-demo-issue-002'];

const DEFAULT_BOUNDARY_TEXT: BoundaryText = BOUNDARY_TEXTS['rsa-demo-issue-003'];

@Component({
  selector: 'app-boundary-review',
  templateUrl: './boundary-review.component.html',
  styleUrls: ['./boundary-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoundaryReviewComponent {
  @Input() issueId = '';
  @Input() issueName = '';
  @Input() issueSeverity = '';
  @Input() issueResourceIcon = '';
  @Output() closed = new EventEmitter<void>();

  public applyState = signal<ApplyState>('idle');
  public windowState = signal<WindowState>('default');

  public onToggleMinimize(): void {
    this.windowState.set(this.windowState() === 'minimized' ? 'default' : 'minimized');
  }

  public onToggleExpand(): void {
    this.windowState.set(this.windowState() === 'expanded' ? 'default' : 'expanded');
  }

  public onClose(): void {
    this.closed.emit();
  }

  public get boundaryText(): BoundaryText {
    return BOUNDARY_TEXTS[this.issueId] || DEFAULT_BOUNDARY_TEXT;
  }

  public onApplyBoundary(): void {
    if (this.applyState() !== 'idle') return;

    this.applyState.set('pending');

    setTimeout(() => {
      this.applyState.set('analyzing');
    }, 1000);

    setTimeout(() => {
      this.applyState.set('validating');
    }, 3000);

    setTimeout(() => {
      this.applyState.set('deploying');
    }, 5000);

    setTimeout(() => {
      this.applyState.set('done');
    }, 7000);
  }
}
